import { z } from 'zod';
import { keccak256, toBytes } from 'viem';
import type { ChecksumAddress } from './address';
import { toChecksumAddress } from './address';
import { NetworkType } from './network';

// Policy types for FE-BE communication (plain strings/enums, no branded types)

const policyTypeSchema = z.enum(['AUTO_APPROVAL', 'MANUAL_APPROVAL']);
export type PolicyType = z.infer<typeof policyTypeSchema>;
export const PolicyTypeEnum = policyTypeSchema.enum;
export const policyTransactionTypeSchema = z.enum([
  'TOKEN_TRANSFER',
  'CONTRACT_INTERACTION',
  'ANY',
]);
export type PolicyTransactionType = z.infer<typeof policyTransactionTypeSchema>;
type PolicyLimitationScope = 'PER_ITEM' | 'ALL_ITEMS';

const policyActorSettingTypeSchema = z.enum(['user', 'group']);
export const PolicyActorSettingTypeEnum = policyActorSettingTypeSchema.enum;

type PolicyAccount = {
  id: string;
  name: string;
  address: string;
};

export type PolicyLimitation = {
  hours: number;
  anchorTimestamp: number;
  initiatorScope: PolicyLimitationScope;
  sourceAccountScope: PolicyLimitationScope;
  destinationScope: PolicyLimitationScope;
};

/**
 * Concrete initiator/account/destination selection for a policy-limitation bucket.
 * This always represents real values (never the bucket `ALL` sentinel).
 */
export type PolicyLimitationSelection = {
  initiatorId: string;
  sourceAccountId: string;
  destination: ChecksumAddress;
};

/**
 * Same selection shape as `PolicyLimitationSelection`, but optional to support
 * partially-selected UI/builder contexts before all dimensions are known.
 */
export type PolicyLimitationSelectedDimensions =
  Partial<PolicyLimitationSelection>;

/**
 * A policy limitation bucket dimension key.
 *
 * Concrete values are canonical identifiers for the dimension:
 * - initiatorKey: user id, e.g. "usr_..." or a UUID-like user id
 * - sourceAccountKey: multichain account id, e.g. "fcd7..."
 * - destinationKey: checksummed address, e.g. "0x1111..."
 *
 * `ALL` is the sentinel for the policy's across-all scope and mirrors the
 * contract-side address(0) bucket for that dimension.
 */
export type PolicyLimitationBucketKeyValue = string | 'ALL';

export type PolicyLimitationBucketKey = {
  initiatorKey: PolicyLimitationBucketKeyValue;
  sourceAccountKey: PolicyLimitationBucketKeyValue;
  destinationKey: PolicyLimitationBucketKeyValue;
};

type BuildRateLimitKeyInput = {
  limitation: PolicyLimitation;
} & Partial<PolicyLimitationSelection>;

/**
 * Builds the scoped bucket key for a transaction under a policy limitation.
 * `ALL` is the off-chain sentinel for the contract's address(0) across-all bucket.
 */
export function buildRateLimitKey({
  limitation,
  initiatorId,
  sourceAccountId,
  destination,
}: BuildRateLimitKeyInput): PolicyLimitationBucketKey {
  let initiatorKey: PolicyLimitationBucketKeyValue;
  // Case: Across-all initiator scope always maps to the ALL sentinel.
  if (limitation.initiatorScope === 'ALL_ITEMS') {
    initiatorKey = 'ALL';
  } else {
    // Case: Per-initiator scopes require a concrete initiator id.
    if (!initiatorId) {
      throw new Error(
        'buildRateLimitKey requires initiatorId when initiatorScope is PER_ITEM'
      );
    }
    initiatorKey = initiatorId;
  }

  let sourceAccountKey: PolicyLimitationBucketKeyValue;
  // Case: Across-all source scope always maps to the ALL sentinel.
  if (limitation.sourceAccountScope === 'ALL_ITEMS') {
    sourceAccountKey = 'ALL';
  } else {
    // Case: Per-source scopes require a concrete source account id.
    if (!sourceAccountId) {
      throw new Error(
        'buildRateLimitKey requires sourceAccountId when sourceAccountScope is PER_ITEM'
      );
    }
    sourceAccountKey = sourceAccountId;
  }

  let destinationKey: PolicyLimitationBucketKeyValue;
  // Case: Across-all destination scope always maps to the ALL sentinel.
  if (limitation.destinationScope === 'ALL_ITEMS') {
    destinationKey = 'ALL';
  } else {
    // Case: Per-destination scopes require a concrete destination key.
    if (!destination) {
      throw new Error(
        'buildRateLimitKey requires destination when destinationScope is PER_ITEM'
      );
    }
    destinationKey = destination;
  }

  return {
    initiatorKey,
    sourceAccountKey,
    destinationKey,
  };
}

type BuildPolicyLimitationUsageKeyInput = {
  policy: Pick<Policy, 'id' | 'limitation'>;
} & PolicyLimitationSelection;

/**
 * Builds the persisted usage key for a rate-limited policy bucket.
 * Threshold changes preserve usage; scope, anchor, interval, or selected bucket
 * changes produce a new key and reset usage for that bucket.
 *
 * NOTE: This intentionally mirrors `mls-contracts` usage-key
 * semantics to avoid offchain/onchain drift. Contracts do not include token
 * identity or amount-threshold fields in the rate-limit usage key domain.
 */
export function buildPolicyLimitationUsageKey({
  policy,
  initiatorId,
  sourceAccountId,
  destination,
}: BuildPolicyLimitationUsageKeyInput): string | null {
  const limitation = policy.limitation;
  if (!limitation) {
    return null;
  }

  // Avoid passing an explicit `destination: undefined` to keep
  // exactOptionalPropertyTypes compatibility for partial selection inputs.
  const rateLimitKey = buildRateLimitKey({
    limitation,
    initiatorId,
    sourceAccountId,
    ...(limitation.destinationScope === 'ALL_ITEMS'
      ? {}
      : { destination: toChecksumAddress(destination) }),
  });
  return keccak256(
    toBytes(
      [
        policy.id,
        limitation.anchorTimestamp,
        limitation.hours,
        limitation.sourceAccountScope,
        rateLimitKey.sourceAccountKey,
        limitation.destinationScope,
        rateLimitKey.destinationKey.toLowerCase(),
        limitation.initiatorScope,
        rateLimitKey.initiatorKey,
      ].join('|')
    )
  );
}

export type PolicyLimitationBucketUsage = {
  key: PolicyLimitationBucketKey;
  initiator?: {
    id: string;
    name: string;
    address: string | null;
  };
  sourceAccount?: {
    id: string;
    name: string;
    address: string | null;
  };
  destination?: {
    address: string;
    name: string | null;
  };
  usedAmount: string;
  remainingAmount: string;
  usedAmountRaw: string;
  remainingAmountRaw: string;
};

// Raw usage row used while assembling and transporting non-enriched usage data.
export type PolicyLimitationBucketUsageRaw = Pick<
  PolicyLimitationBucketUsage,
  'key' | 'usedAmountRaw' | 'remainingAmountRaw'
>;

export type PolicyLimitationUsage = {
  windowStart: Date;
  windowEnd: Date;
  amountThreshold: string;
  tokenDecimals: number | null;
  buckets: PolicyLimitationBucketUsage[];
  selectedBucket: PolicyLimitationBucketUsage | null;
};

// Raw usage payload before enrichment with labels and formatted amounts.
export type PolicyLimitationUsageRaw = Omit<
  PolicyLimitationUsage,
  'buckets' | 'selectedBucket'
> & {
  buckets: PolicyLimitationBucketUsageRaw[];
  selectedBucket: PolicyLimitationBucketUsageRaw | null;
};

// Backward-compatible alias for pre-existing "View" naming at call sites.
export type PolicyLimitationUsageView = PolicyLimitationUsageRaw;

/**
 * Minimal usage context used by policy validation for rate-limit checks.
 * Optional fields support progressive enrichment when not all window data exists.
 */
export type RateLimitUsageContext = {
  usedAmountRaw: string;
  amountThresholdRaw?: string;
  windowStart?: string;
  windowEnd?: string;
  usageKey?: string;
};

export type PolicyRateLimitInfo = {
  rateLimitReached: boolean;
  resetAt: Date | null;
};

export type PolicyWithRateLimitInfo = Policy & {
  rateLimitInfo: PolicyRateLimitInfo;
};

// Initiator setting - discriminated union: either a user or a group
// Names are enriched by use case for frontend display
export type PolicyInitiatorSetting =
  | {
      type: typeof PolicyActorSettingTypeEnum.user;
      userId: string;
      userName: string;
    }
  | {
      type: typeof PolicyActorSettingTypeEnum.group;
      groupId: string;
      groupName: string;
      members: Array<{ id: string; name: string }>;
    };

// Approver setting - discriminated union: user or group with threshold
// Names are enriched by use case for frontend display
export type PolicyApproverSetting =
  | {
      type: typeof PolicyActorSettingTypeEnum.user;
      userId: string;
      userName: string;
    }
  | {
      type: typeof PolicyActorSettingTypeEnum.group;
      groupId: string;
      groupName: string;
      threshold: number;
      members: Array<{ id: string; name: string }>;
    };

export type PolicyDestinationAddressEntry = {
  address: string;
  name: string;
  type: 'UNKNOWN' | 'EOA' | 'CONTRACT' | 'MLSACCOUNT';
};

// Token transfer condition - enriched with token details from BE
// tokenId is the internal reference, tokenAddress is the contract address for API/explorer display
export type PolicyTokenTransferCondition = {
  tokenId: string | null;
  tokenAddress: string | null;
  tokenSymbol: string | null;
  tokenName: string | null;
  tokenLogoUrl: string | null;
  tokenDecimals: number | null;
  amountThreshold: string | null;
  destinationAddresses: string[];
  destinationAddressEntries?: PolicyDestinationAddressEntry[];
};

/**
 * A contract function with its 4-byte selector for policy conditions.
 * Overloaded names appear as separate entries with different selectors.
 */
export type PolicyFunction = {
  name: string; // Function name (e.g., "transfer", "approve")
  selector: string; // 4-byte selector (e.g., "0xa9059cbb")
};

type PolicyContractAddressCondition = {
  contractAddress: string;
  proxyFunctions: PolicyFunction[];
  implementationFunctions: PolicyFunction[];
};

export type PolicyContractInteractionCondition =
  PolicyContractAddressCondition[];

// Base policy fields
type PolicyBase = {
  id: string;
  // On-chain ID (UUID) for Merkle tree. Never query by this - use `id` instead.
  onchainId: string;
  name: string;
  description: string | null;
  type: PolicyType;
  networkId: NetworkType;
  organizationId: string;
  accounts: PolicyAccount[];
  limitation: PolicyLimitation | null;
  limitationUsage?: PolicyLimitationUsage | null;
  initiatorSetting: PolicyInitiatorSetting | null;
  approverSetting: PolicyApproverSetting | null;
  isDeleted: boolean;
  createdAt: Date;
  updatedAt: Date;
};

// Discriminated union based on transactionType
type TokenTransferPolicy = PolicyBase & {
  transactionType: 'TOKEN_TRANSFER';
  tokenTransferCondition: PolicyTokenTransferCondition | null;
};

type ContractInteractionPolicy = PolicyBase & {
  transactionType: 'CONTRACT_INTERACTION';
  contractInteractionCondition: PolicyContractInteractionCondition | null;
};

type AnyPolicy = PolicyBase & {
  transactionType: 'ANY';
};

export type Policy =
  | TokenTransferPolicy
  | ContractInteractionPolicy
  | AnyPolicy;
